import type { ModuleEventDef } from "@vavr/core";
import { defineModule } from "../define.ts";

/**
 * Emergency mode (PMU-273): declare a lockdown, evacuation, shelter or reunification (a drill or
 * real), account for every child, report each class, end it. The records are signed events in the
 * school's chain like every other; with the internet and the Wi-Fi down they travel over the
 * Bluetooth mesh and reach the chain later (docs/plan/emergency-mesh.md). Releases during
 * reunification are the core `pickup` with the `reunification` flag.
 */
export const EMERGENCY_KINDS = ["lockdown", "evacuation", "shelter", "reunification"] as const;
export const ROLL_CALL = ["present", "missing", "injured", "with_adult", "absent_today"] as const;
export const EMERGENCY_ALERTS = ["help", "medical", "all_clear"] as const;

const str = (v: unknown, max: number) => typeof v === "string" && v.length <= max;
const incident = (d: Record<string, unknown>) => (typeof d.incident === "string" && /^[A-Za-z0-9_-]{8,64}$/.test(d.incident) ? [] : ["data.incident: the incident's id"]);
const data = (pre: Readonly<Record<string, unknown>>) => ((pre.data && typeof pre.data === "object") ? pre.data as Record<string, unknown> : {});

const events: ModuleEventDef[] = [
  {
    type: "emergency:declare", label: "Emergency declared", child: "none", staff: "required",
    validate: (pre) => {
      const d = data(pre); const e = incident(d);
      if (!EMERGENCY_KINDS.includes(d.kind as never)) e.push("data.kind: lockdown, evacuation, shelter or reunification");
      if (typeof d.drill !== "boolean") e.push("data.drill: a drill or a real emergency");
      if (d.place != null && !str(d.place, 120)) e.push("data.place: up to 120 characters");
      if (d.note != null && !str(d.note, 500)) e.push("data.note: up to 500 characters");
      return e;
    },
  },
  {
    type: "emergency:end", label: "Emergency ended", child: "none", staff: "required",
    validate: (pre) => { const d = data(pre); const e = incident(d); if (d.note != null && !str(d.note, 500)) e.push("data.note: up to 500 characters"); return e; },
  },
  {
    type: "emergency:roll_call", label: "Accounted for in an emergency", child: "required", staff: "required",
    validate: (pre) => {
      const d = data(pre); const e = incident(d);
      if (!ROLL_CALL.includes(d.status as never)) e.push("data.status: present, missing, injured, with_adult or absent_today");
      if (d.place != null && !str(d.place, 120)) e.push("data.place: up to 120 characters");
      if (d.note != null && !str(d.note, 500)) e.push("data.note: up to 500 characters");
      return e;
    },
  },
  {
    type: "emergency:class_report", label: "Class reported in an emergency", child: "none", staff: "required",
    validate: (pre) => {
      const d = data(pre); const e = incident(d);
      if (d.room_id != null && !str(d.room_id, 64)) e.push("data.room_id");
      if (!Number.isInteger(d.accounted) || (d.accounted as number) < 0) e.push("data.accounted: how many children are accounted for");
      // Event data is flat (strings, numbers, booleans): the missing children's ids, comma-separated.
      if (typeof d.missing !== "string" || d.missing.length > 4000 || (d.missing !== "" && !d.missing.split(",").every((x) => /^[A-Za-z0-9_-]{1,64}$/.test(x)))) e.push("data.missing: the missing children's ids, comma-separated");
      if (typeof d.complete !== "boolean") e.push("data.complete: every child of the class accounted for");
      return e;
    },
  },
  {
    // E4: a text to every member of staff, or to one (data.to: their account id; the mesh carries it
    // to every device, the apps show it to the one it is for).
    type: "emergency:message", label: "Emergency message", child: "none", staff: "required",
    validate: (pre) => {
      const d = data(pre); const e = incident(d);
      if (typeof d.text !== "string" || !d.text.trim() || d.text.length > 500) e.push("data.text: 1 to 500 characters");
      if (d.to != null && !(typeof d.to === "string" && /^[A-Za-z0-9_-]{1,64}$/.test(d.to))) e.push("data.to: a member of staff's id");
      return e;
    },
  },
  {
    // E4: a fixed priority alert: help needed, a medical emergency, or all clear where I am.
    type: "emergency:alert", label: "Emergency alert", child: "none", staff: "required",
    validate: (pre) => {
      const d = data(pre); const e = incident(d);
      if (!EMERGENCY_ALERTS.includes(d.alert as never)) e.push("data.alert: help, medical or all_clear");
      if (d.place != null && !str(d.place, 120)) e.push("data.place: up to 120 characters");
      return e;
    },
  },
];

export const emergency = defineModule({
  id: "emergency",
  name: "Emergency mode",
  version: "0.2.0",
  description: "Declare a lockdown, evacuation, shelter or reunification (drill or real), account for every child and reunify, even with the internet and the Wi-Fi down.",
  core: true,
  // Any teacher or member of staff may declare or end one (owner, 2026-10-05).
  grants: { "emergency:declare": ["org_admin", "site_admin", "door_staff", "teacher"] },
  events,
});
